<!-- 
*****************************************************************************
**                                                                         **
** Copyright (c) 2012-2024, Tyker Technology BV                            **
** All rights reserved.                                                    **
**                                                                         **
** This software is the confidential and proprietary information of        **
** Tyker Technology BV. Unauthorized copying or disclosure of this file,   **
** via any medium is strictly prohibited. This file is only available to   **
** authorized individuals with the permission of the copyright holders.    **
** If you encounter this file and do not have permission, please contact   ** 
** the copyright holders and delete this file.                             **
**                                                                         **
***************************************************************************** 
-->

<template>
  <div>
    <v-progress-circular v-if="loading" indeterminate></v-progress-circular>
    <div
      v-if="!loading && !((steeringlineProduction != null && steeringlineProduction.length > 0) || pointProduction.length > 0) && !placeholder"
      class="py-3">
      {{ language.t("RobotPlotter.RbpProductionList.noProductionAvailable") }}
    </div>
    <div v-else-if="placeholder">
      {{ language.t("RobotPlotter.RbpProductionList.productionPlaceholderText") }}
    </div>
    <div v-else-if="!loading">
      <v-btn-toggle v-model="formatToggle" mandatory>

        <v-btn value="summary">
          {{ language.t("RobotPlotter.RbpProductionList.summary") }}
        </v-btn>
        <v-btn v-if="steeringlineProduction.length != 0" value="steeringlines">
          {{ language.t("RobotPlotter.RbpProductionList.steeringlinesButton") }}
        </v-btn>
        <v-btn v-if="pointProduction.length != 0" value="points">
          {{ language.t("RobotPlotter.RbpProductionList.points") }}
        </v-btn>
      </v-btn-toggle>

      <div v-if="format != 'summary'">
        <v-data-table class="production-details elevation-0 py-1" v-model="selected" dense single-select fixed-header
          :items="items" :headers="headers"
          :item-key="format == 'steeringlines' ? 'rptimepositionlogId' : 'rptimepositionId'" :hide-default-footer="true"
          :options="{ itemsPerPage: -1 }" @click:row="setSelectedFromTable">
          <template v-slot:[`header.taskSprayInterval`]="{ header }">
            <span v-html="header.text"></span>
          </template>
        </v-data-table>
      </div>
      <div v-else>
        <b class="py-3 text-h5">
          {{ language.t("RobotPlotter.RbpProductionList.totalproduction") }} {{ totalProductionSteeringlines.toFixed(2)
            > 0 ?
            totalProductionSteeringlines.toFixed(2).toString() + unit : ""
          }}
          {{ totalProductionSteeringlines.toFixed(2) > 0 && totalProductionPoints > 0 ? ", " : "" }}{{
            totalProductionPoints > 0 ?
              totalProductionPoints.toString() + " points" : "" }}
        </b>
        <div v-for="device in summaryProduction" :key="device.deviceId">
          <b class="py-3 text-h6">
            {{ device.deviceName }} (total:
            {{ device.totalProductionSteeringlines.toFixed(2) > 0 ?
              device.totalProductionSteeringlines.toFixed(2).toString() + unit : ""
            }}{{ device.totalProductionSteeringlines.toFixed(2) > 0 && device.totalProductionPoints > 0 ? ", " : "" }}{{
              device.totalProductionPoints > 0 ?
                device.totalProductionPoints.toString() + " points" : "" }})

          </b>
          <v-data-table dense :items="device.days" :headers="headers" item-key="date" class="elevation-0 py-1"
            :hide-default-footer="true" :options="{ itemsPerPage: -1 }">
            <template v-slot:[`item.steeringlines`]="{ item }">
              {{ item.steeringlines.toFixed(2) }}
            </template>
          </v-data-table>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from "moment/min/moment-with-locales";

export default {
  data: () => ({
    loading: false,
    selected: [],
    requestCancelToken: null,
    machineNames: [],
    pointProduction: [],
    steeringlineProduction: [],
    summaryProduction: [],
    productionLength: "0:00:00",
    totalProductionSteeringlines: 0,
    totalProductionPoints: 0,
    formatToggle: "",
    format: "",
    unit: "test",
    factor: 1,

    items: [],
    headers: [],
    asBuildExport: false,
    isPointProduction: false,

    placeholder: false,
  }),
  props: {},
  watch: {
    formatToggle(val) {
      this.setTableFormat(val)
    },
    $selectedUnitSystem(val,oldVal) {
      console.log(val,oldVal)
    }
  },
  mounted: function () { },
  methods: {
    setSelectedFromList(id) {
      this.$emit("setSelectedFromList", id);
    },
    setSelectedFromLine(id) {
      this.selected = [];
      var selectedLine = this.items.find((item) => {
        if (item.rptimepositionlogId) {
          return String(item.rptimepositionlogId) == id;
        } else {
          return String(item.rptimepositionId) == id;
        }
      });
      this.selected.push(selectedLine);
      console.log(this.selected);
    },
    deselectRow() {
      this.selected = [];
    },
    setSelectedFromTable(item, row) {
      let selectState = row.isSelected ? false : true;
      row.select(selectState);
      console.log(item)
      var id = item.rptimepositionlogId ? item.rptimepositionlogId : item.rptimepositionId;
      this.setSelectedFromList(id);
    },
    async getProduction(devices = null, taskId = null, start = null, end = null, target = "LIST", floating = false, callback = null) {
      if (this.$selectedUnitSystem != "me") {
        this.factor = 3.2808399 // change to use unit from db
      }

      this.loading = true;
      this.asBuildExport = false;
      this.requestCancelToken = this.$axios.CancelToken.source();

      //get device names
      const { data: deviceNames } = await this.$axios.get(route("api.devices"));
      this.machineNames = deviceNames;

      const data = { target: target };
      if (devices) data.deviceSelection = devices;
      if (taskId) data.taskId = taskId;
      if (start) data.startDate = start;
      if (end) data.endDate = end;

      //get rptimepositionlogs
      const { data: production } = await this.$axios.post(route("api.production.rbp"), data, { cancelToken: this.requestCancelToken.token }).catch((err) => {
        console.log(err);
        if (callback) callback(false);
      });

      //get rptimepositions for points
      var rptimepositionlogIds = [];
      production.production.forEach((production) => {
        if (production.roadplottertaskstateId == 6) {
          rptimepositionlogIds.push(production.rptimepositionlogId);
        }
      });
      var timepositionlogs = await this.getRpTimePositionLogs(rptimepositionlogIds);

      //add rptimepositions to rptimepositionlogs
      production.production.forEach((production) => {
        production["rptimepositions"] = timepositionlogs[production.rptimepositionlogId];
      });

      production.production.forEach((rptimepositionlog) => {
        switch (rptimepositionlog.roadplottertaskstate.description) {
          case "__RP_STATE_SURVEY":
            if (floating) {
              this.asBuildExport = true;
            }
            break;
          case "__RP_STATE_SPRAY_ON":
            this.addSteeringlineProduction(rptimepositionlog);
            this.addSummaryProduction(rptimepositionlog);
            break;
          case "__RP_STATE_POINT":
            if (floating) {
              this.isPointProduction = true;
            }
            if (rptimepositionlog.rptimepositions) {
              this.addPointProduction(rptimepositionlog);
              this.addSummaryProduction(rptimepositionlog);
            } else {
              console.warn('rptimepositionlog has no rptimepositions');
              console.warn(rptimepositionlog);
            }
            break;
          default:
            break;
        }
      });

      this.formatToggle = 'summary'
      this.loading = false;

      if (callback) callback(true);
    },
    async getRpTimePositionLogs(rptimepositionlogIds) {
      var deferreds = {};
      var rptimepositionlogsToHandle = [];
      var maxRpTimePositionlogsToHandle = 100;
      var rpTimePositionLogsHandled = 0;
      var promises = [];

      // Iteratie over RpTimePositionLogs
      var i = 0;
      var len = rptimepositionlogIds.length;
      for (let j = 0; j < rptimepositionlogIds.length; j++) {
        rptimepositionlogsToHandle.push(rptimepositionlogIds[j]);
        rpTimePositionLogsHandled++;

        if (rpTimePositionLogsHandled > maxRpTimePositionlogsToHandle || i === len - 1) {
          promises.push(this.addRpTimePositionLogs(rptimepositionlogsToHandle).then(positions => {
            deferreds = Object.assign(deferreds, positions);
          }));
          rptimepositionlogsToHandle = [];
          rpTimePositionLogsHandled = 0;
        }
        i++;
      }
      await Promise.all(promises)
      return deferreds;
    },
    async addRpTimePositionLogs(rptimepositionlogIds) {
      const { data: positions } = await this.$axios.get(route("api.production.rbp.positions"), { params: { rptimepositionlogs: rptimepositionlogIds } });
      return positions;
    },
    addSteeringlineProduction(rpTimePositionLog) {
      var sprayPattern;
      if (rpTimePositionLog.taskSprayInterval == 0.0 && rpTimePositionLog.taskSprayLength == 0.0) {
        sprayPattern = this.language.t('RobotPlotter.RbpProductionList.continuousSpray');
      } else if ((rpTimePositionLog.taskSprayInterval > 0.00) && (rpTimePositionLog.taskSprayLength == 0.0 || rpTimePositionLog.taskSprayLength == null || (!Number.isFinite(rpTimePositionLog.taskSprayLength)))) {
        sprayPattern = (rpTimePositionLog.taskSprayInterval * this.factor).toFixed(2);
      } else {
        sprayPattern = (rpTimePositionLog.taskSprayInterval * this.factor).toFixed(2) + " / " + (rpTimePositionLog.taskSprayLength * this.factor).toFixed(2);
      }

      this.steeringlineProduction.push({
        rptimepositionlogId: rpTimePositionLog.rptimepositionlogId,
        device: this.getMachineName(rpTimePositionLog.device_accountId),
        taskName: rpTimePositionLog.timepositionlog.task_name,
        startRptimepositionTimestamp: this.getDateFormat(rpTimePositionLog.start_rptimeposition_timestamp),
        endRptimepositionTimestamp: this.getDateFormat(rpTimePositionLog.end_rptimeposition_timestamp),
        // distance
        taskSprayInterval: sprayPattern,
        taskLineLength: (rpTimePositionLog.taskLineLength * this.factor).toFixed(2),
      });
    },
    addPointProduction(rptimepositionlog) {
      rptimepositionlog.rptimepositions.forEach(rptimeposition => {
        this.pointProduction.push({
          rptimepositionId: rptimeposition.rptimepositionId,
          taskName: rptimepositionlog.timepositionlog.task_name,
          device: this.getMachineName(rptimepositionlog.device_accountId),
          timestamp: rptimeposition.timestamp,
          xspray: rptimeposition.xspray,
          yspray: rptimeposition.yspray,
          zspray: rptimeposition.zspray,
        })
      });
    },
    addSummaryProduction(rptimepositionlog) {
      var date = moment(rptimepositionlog.start_rptimeposition_timestamp).format('YYYY-MM-DD'); /// date in YYYY/mm/dd format
      var firstTime = moment(rptimepositionlog.start_rptimeposition_timestamp); //datetime
      var lastTime = moment(rptimepositionlog.end_rptimeposition_timestamp); //datetime

      var timeDiff = lastTime.diff(firstTime); //ms
      this.productionLength = this.sumTimes(this.productionLength, this.formatTime(timeDiff));

      var device = this.getMachineName(rptimepositionlog.device_accountId);

      var pointCount = 0;
      var steeringlinesLength = 0;
      if (rptimepositionlog.roadplottertaskstateId == 6) {
        pointCount = rptimepositionlog.rptimepositions.length;
      } else {
        steeringlinesLength = (Number(rptimepositionlog.taskLineLength)) * this.factor;
      }

      this.totalProductionSteeringlines += steeringlinesLength;
      this.totalProductionPoints += pointCount;

      var summaryDeviceIndex = this.summaryProduction.findIndex(device => { return device.deviceId == rptimepositionlog.device_accountId });
      if (summaryDeviceIndex != -1) {
        var summaryDayIndex = this.summaryProduction[summaryDeviceIndex].days.findIndex(day => day.date == date);
        if (summaryDayIndex != -1) {
          this.summaryProduction[summaryDeviceIndex].days[summaryDayIndex].steeringlines += steeringlinesLength;
          this.summaryProduction[summaryDeviceIndex].days[summaryDayIndex].points += pointCount;

          this.summaryProduction[summaryDeviceIndex].days[summaryDayIndex].time = this.sumTimes(this.summaryProduction[summaryDeviceIndex].days[summaryDayIndex].time, this.formatTime(timeDiff));

          this.summaryProduction[summaryDeviceIndex].length = this.sumTimes(this.summaryProduction[summaryDeviceIndex].length, this.formatTime(timeDiff));
          this.summaryProduction[summaryDeviceIndex].totalProductionPoints += pointCount;
          this.summaryProduction[summaryDeviceIndex].totalProductionSteeringlines += steeringlinesLength;
        } else {
          this.summaryProduction[summaryDeviceIndex].days.push({ date: date, time: this.formatTime(timeDiff), steeringlines: steeringlinesLength, points: pointCount });
          this.summaryProduction[summaryDeviceIndex].length = this.sumTimes(this.summaryProduction[summaryDeviceIndex].length, this.formatTime(timeDiff));
          this.summaryProduction[summaryDeviceIndex].totalProductionPoints += pointCount;
          this.summaryProduction[summaryDeviceIndex].totalProductionSteeringlines += steeringlinesLength;
        }
      } else {
        this.summaryProduction.push(
          {
            deviceId: rptimepositionlog.device_accountId,
            deviceName: device,
            length: this.formatTime(timeDiff),
            days: [
              { date: date, time: this.formatTime(timeDiff), steeringlines: steeringlinesLength, points: pointCount }
            ],
            totalProductionPoints: pointCount,
            totalProductionSteeringlines: steeringlinesLength
          }
        )
      }
    },
    sumTimes(time1, time2) {
      var totalSeconds = 0;
      var times = [time1, time2];
      times.forEach((time) => {
        var splitTime = time.split(":");
        totalSeconds += Number(splitTime[0]) * 3600;
        totalSeconds += Number(splitTime[1]) * 60;
        totalSeconds += Number(splitTime[2]);
      });
      var hours = Math.floor(totalSeconds / 3600);
      totalSeconds -= hours * 3600;
      var minutes = Math.floor(totalSeconds / 60);
      totalSeconds -= minutes * 60;

      var result = hours.toString() + ":" + ("0" + minutes).slice(-2) + ":" + ("0" + totalSeconds).slice(-2);
      return result;
    },
    formatTime(time) {
      var seconds = time / 1000;
      var hours = Math.floor(seconds / 3600);
      seconds -= hours * 3600;
      var minutes = Math.floor(seconds / 60);
      seconds -= minutes * 60;

      var result = hours.toString() + ":" + ("0" + minutes).slice(-2) + ":" + ("0" + seconds).slice(-2);
      return result;
    },
    getDateFormat(timestamp) {
      var date = new Date(timestamp);
      var timeZoneOffset = date.getTimezoneOffset();
      date.setMinutes(date.getMinutes() + timeZoneOffset);
      return date.toLocaleString(this.language.t("App.locale"));
    },
    getMachineName(accountId) {
      var index = this.machineNames.findIndex((machine) => {
        return machine.accountId === accountId;
      });
      if (index != -1) return this.machineNames[index].serialNumber + " - " + this.machineNames[index].designator;
      return accountId;
    },
    setTableFormat(format) {
      if (this.$selectedUnitSystem == "me") {
        this.unit = " [m]"
      } else {
        this.unit = " [ft]"
      }

      this.format = format;
      switch (format) {
        case "summary":
          this.headers = [
            {
              text: this.language.t('RobotPlotter.RbpProductionList.date'),
              align: "start",
              sortable: false,
              value: "date",
            },
            {
              text: this.language.t('RobotPlotter.RbpProductionList.duration'),
              align: "start",
              sortable: false,
              value: "time"
            },
          ];
          if (this.totalProductionSteeringlines > 0) {
            this.headers.push({
              text: this.language.t('RobotPlotter.RbpProductionList.steeringlines') + this.unit,
              align: "start",
              sortable: false,
              value: "steeringlines",
            })
          }
          if (this.totalProductionPoints > 0) {
            this.headers.push({
              text: this.language.t('RobotPlotter.RbpProductionList.points'),
              align: "start",
              sortable: false,
              value: "points",
            });
          }
          break;
        case "steeringlines":
          this.headers = [
            {
              text: this.language.t('RobotPlotter.RbpProductionList.id'),
              align: "start",
              sortable: false,
              value: "rptimepositionlogId",
            },
            {
              text: this.language.t('RobotPlotter.RbpProductionList.device'),
              align: "start",
              sortable: false,
              value: "device",
            },
            {
              text: this.language.t('RobotPlotter.RbpProductionList.task'),
              align: "start",
              sortable: false,
              value: "taskName",
            },
            {
              text: this.language.t('RobotPlotter.RbpProductionList.start'),
              align: "start",
              sortable: false,
              value: "startRptimepositionTimestamp",
            },
            {
              text: this.language.t('RobotPlotter.RbpProductionList.end'),
              align: "start",
              sortable: false,
              value: "endRptimepositionTimestamp",
            },
            {
              //            "taskSprayInterval": "Spuitpatroon [m]<br>Interval / spuitlengte",
              text: this.language.t('RobotPlotter.RbpProductionList.taskSprayPatern') + this.unit
                + "<br>" + this.language.t('RobotPlotter.RbpProductionList.taskSprayLength') + this.unit,
              align: "start",
              sortable: false,
              value: "taskSprayInterval",
            },
            {
              text: this.language.t('RobotPlotter.RbpProductionList.taskLineLength') + this.unit,
              align: "start",
              sortable: false,
              value: "taskLineLength",
            },
          ];
          this.items = this.steeringlineProduction;

          break;
        case "points":
          this.headers = [
            {
              text: this.language.t('RobotPlotter.RbpProductionList.id'),
              align: "start",
              sortable: false,
              value: "rptimepositionId",
            },
            {
              text: this.language.t('RobotPlotter.RbpProductionList.task'),
              align: "start",
              sortable: false,
              value: "taskName",
            },
            {
              text: this.language.t('RobotPlotter.RbpProductionList.device'),
              align: "start",
              sortable: false,
              value: "device",
            },
            {
              text: this.language.t('RobotPlotter.RbpProductionList.timestamp'),
              align: "start",
              sortable: false,
              value: "timestamp",
            },
            {
              text: "X",
              align: "start",
              sortable: false,
              value: "xspray",
            },
            {
              text: "Y",
              align: "start",
              sortable: false,
              value: "yspray",
            },
            {
              text: "Z",
              align: "start",
              sortable: false,
              value: "zspray",
            }
          ];
          this.items = this.pointProduction;

          break;
        default:
          break;
      }

    },
    empty() {
      console.log("Empty production table");
      Object.assign(this.$data, this.$options.data.apply(this));

      if (this.requestCancelToken) this.requestCancelToken.cancel();
    },
    isEmpty() {
      if (this.loading) return false;
      if (this.steeringlineProduction == null || this.steeringlineProduction.length == 0) var productionIsNull = true;
      if (this.pointProduction.length == 0) var pointsProductionIsNull = true;
      if (this.placeholder) return false;

      if (productionIsNull && pointsProductionIsNull) return true;
      return false;
    },

  },
  computed: {},
};
</script>
<style scoped>
.v-data-table>>>.v-data-table__selected {
  background: #8dbde4 !important;
}

.production-details>>>tbody tr :hover {
  cursor: pointer;
}
</style>